import React from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { 
  BarChart3, 
  TrendingUp,
  TrendingDown, 
  Target, 
  Users, 
  Globe, 
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  Eye,
  MousePointer,
  Activity
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

interface BaseReportTemplateProps {
  reportData: any;
  reportType: string;
  reportName: string;
  reportDescription?: string;
}

export function BaseReportTemplate({ 
  reportData, 
  reportType, 
  reportName, 
  reportDescription 
}: BaseReportTemplateProps) {
  // Helper function to format dates from different formats
  const formatChartDate = (value: any): string => {
    // Handle different date formats
    let date;
    if (typeof value === 'string' && value.length === 8) {
      // Format: "20250522" -> "2025-05-22"
      const year = value.substring(0, 4);
      const month = value.substring(4, 6);
      const day = value.substring(6, 8);
      date = new Date(`${year}-${month}-${day}`);
    } else {
      date = new Date(value);
    }
    
    if (isNaN(date.getTime())) {
      return String(value); // Return original if can't parse
    }
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Icon mapping for different report types
  const getReportTypeIcon = (type: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      'analytics': BarChart3,
      'performance': TrendingUp,
      'conversion': Target,
      'audience': Users,
      'seo': Globe,
      'social': Users,
      'custom': FileText
    };
    return iconMap[type] || FileText;
  };

  // Color mapping for different report types
  const getReportTypeColor = (type: string) => {
    const colorMap: Record<string, string> = {
      'analytics': 'blue',
      'performance': 'green',
      'conversion': 'purple',
      'audience': 'orange',
      'seo': 'teal',
      'social': 'pink',
      'custom': 'gray'
    };
    return colorMap[type] || 'gray';
  };

  // Improved metric formatting helpers
  const formatMetricValue = (value: any, metricKey: string): string => {
    if (value === null || value === undefined) return 'N/A';
    
    // If value is already a formatted string (contains commas or special formatting), return as-is
    if (typeof value === 'string') {
      // Check if it's already a formatted number with commas or a percentage
      if (value.includes(',') || value.includes('%') || value.includes('$') || 
          value.includes('h') || value.includes('m') || value.includes('s')) {
        return value;
      }
      
      // Try to parse as number for further formatting
      const numericValue = parseFloat(value.replace(/,/g, ''));
      if (isNaN(numericValue)) {
        return value; // Return original string if can't parse
      }
      
      // Continue with numeric formatting below
      value = numericValue;
    }
    
    // If it's not a valid number, return as string
    if (typeof value !== 'number' || isNaN(value)) {
      return String(value);
    }
    
    // Handle percentages (bounce rate, conversion rate)
    if (metricKey.toLowerCase().includes('rate') || 
        metricKey.toLowerCase().includes('percentage') ||
        metricKey === 'bounceRate' ||
        metricKey === 'conversionRate') {
      // If value is between 0 and 1, it's a decimal percentage
      if (value <= 1) {
        return `${(value * 100).toFixed(1)}%`;
      }
      // If value is already a percentage (> 1), just add %
      return `${value.toFixed(1)}%`;
    }
    
    // Handle duration/time metrics
    if (metricKey.toLowerCase().includes('duration') ||
        metricKey.toLowerCase().includes('time') ||
        metricKey.includes('sessionDuration')) {
      if (value > 3600) {
        return `${(value / 3600).toFixed(1)}h`;
      } else if (value > 60) {
        return `${(value / 60).toFixed(1)}m`;
      } else {
        return `${Math.round(value)}s`;
      }
    }
    
    // Handle large numbers (sessions, users, pageviews)
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    
    // Default number formatting
    return value.toLocaleString();
  };

  // Get metric display name
  const getMetricDisplayName = (key: string): string => {
    const nameMap: Record<string, string> = {
      'sessions': 'Sessions',
      'users': 'Users',
      'pageviews': 'Page Views',
      'bounceRate': 'Bounce Rate',
      'sessionDuration': 'Avg. Session Duration',
      'conversionRate': 'Conversion Rate',
      'pagesPerSession': 'Pages/Session',
      'sessionsPerUser': 'Sessions/User',
      'totalConversions': 'Total Conversions',
      'conversionValue': 'Conversion Value',
      'costPerConversion': 'Cost Per Conversion',
      'avgConversionValue': 'Avg. Conversion Value',
      'totalUsers': 'Total Users',
      'newUsers': 'New Users',
      'returningUsers': 'Returning Users',
      'userRetention': 'User Retention',
      'avgSessionDuration': 'Avg. Session Duration',
      'totalRevenue': 'Total Revenue',
      'revenuePerSession': 'Revenue Per Session',
      'organicSessions': 'Organic Sessions',
      'avgPosition': 'Avg. Position',
      'totalImpressions': 'Total Impressions',
      'clickThroughRate': 'Click Through Rate'
    };
    
    return nameMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  // Get metric icon
  const getMetricIcon = (key: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      'sessions': Activity,
      'users': Users,
      'pageviews': Eye,
      'bounceRate': TrendingDown,
      'sessionDuration': Clock,
      'conversionRate': Target,
      'totalConversions': Target,
      'avgLoadTime': Clock,
      'organicSessions': Globe,
      'socialSessions': Users,
      'engagementRate': MousePointer
    };
    return iconMap[key] || BarChart3;
  };

  // Enhanced metric card with better styling
  const renderMetricCard = (key: string, value: any) => {
    const IconComponent = getMetricIcon(key);
    const color = getReportTypeColor(reportType);
    const formattedValue = formatMetricValue(value, key);
    
    // Generate a mock trend for visual appeal (in real app, this would come from data)
    const isPositiveTrend = Math.random() > 0.5;
    const trendValue = (Math.random() * 20).toFixed(1);
    
    return (
      <Card key={key} className="p-6 hover:shadow-lg transition-shadow duration-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{getMetricDisplayName(key)}</h3>
          <div className={`w-10 h-10 bg-gradient-to-r from-${color}-500 to-${color}-600 rounded-xl flex items-center justify-center shadow-lg`}>
            <IconComponent className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {formattedValue}
          </div>
          <div className="flex items-center space-x-2">
            {isPositiveTrend ? (
              <TrendingUp className="w-4 h-4 text-green-600" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-600" />
            )}
            <span className={`text-sm font-medium ${isPositiveTrend ? 'text-green-600' : 'text-red-600'}`}>
              {isPositiveTrend ? '+' : '-'}{trendValue}%
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">vs last period</span>
          </div>
        </div>
      </Card>
    );
  };

  // Enhanced chart rendering with proper colors and styling
  const renderChart = (chart: any, index: number) => {
    if (!chart || !chart.data || chart.data.length === 0) {
      return (
        <Card key={index} className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{chart?.title || 'Chart'}</h3>
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
              <p>No data available</p>
            </div>
          </div>
        </Card>
      );
    }

    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

    switch (chart.type) {
      case 'line':
        return (
          <Card key={index} className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{chart.title}</h3>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chart.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={formatChartDate}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  labelFormatter={(value) => formatChartDate(value)}
                  formatter={(value: any) => [value.toLocaleString(), 'Sessions']}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: "#3b82f6", strokeWidth: 2, fill: "white" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        );

      case 'area':
        return (
          <Card key={index} className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{chart.title}</h3>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={chart.data}>
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={formatChartDate}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  labelFormatter={(value) => formatChartDate(value)}
                  formatter={(value: any) => [value.toLocaleString(), 'Value']}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  fill="url(#colorGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        );

      case 'bar':
        return (
          <Card key={index} className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{chart.title}</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chart.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="name" 
                  stroke="#64748b"
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value: any) => [value.toLocaleString(), 'Value']}
                />
                <Bar 
                  dataKey="value" 
                  fill="#3b82f6" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        );

      case 'pie':
        return (
          <Card key={index} className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{chart.title}</h3>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={chart.data}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  labelLine={false}
                >
                  {chart.data.map((entry: any, entryIndex: number) => (
                    <Cell 
                      key={`cell-${entryIndex}`} 
                      fill={colors[entryIndex % colors.length]} 
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value: any) => [value.toLocaleString(), 'Sessions']}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        );

      default:
        return null;
    }
  };

  // Enhanced table rendering with better styling
  const renderTable = (table: any, index: number) => {
    if (!table || !table.rows || table.rows.length === 0) {
      return (
        <Card key={index} className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{table?.title || 'Data Table'}</h3>
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
            <p>No data available</p>
          </div>
        </Card>
      );
    }

    return (
      <Card key={index} className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{table.title}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200 dark:border-gray-600">
                {table.headers.map((header: string, headerIndex: number) => (
                  <th key={headerIndex} className="text-left py-4 px-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 first:rounded-tl-lg last:rounded-tr-lg">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row: any, rowIndex: number) => (
                <tr key={rowIndex} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  {table.headers.map((header: string, colIndex: number) => (
                    <td key={colIndex} className="py-4 px-4 text-gray-900 dark:text-gray-100">
                      {Array.isArray(row) 
                        ? (typeof row[colIndex] === 'number' ? row[colIndex].toLocaleString() : row[colIndex])
                        : (typeof row[header] === 'number' ? row[header].toLocaleString() : row[header])
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  // Enhanced insights rendering
  const renderInsights = (insights: any) => {
    if (!insights) return null;

    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            AI Insights
          </div>
        </h3>
        
        {insights.summary && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
            <h4 className="font-medium text-blue-900 mb-2">Summary</h4>
            <p className="text-blue-800 leading-relaxed">{insights.summary}</p>
          </div>
        )}

        {insights.keyFindings && insights.keyFindings.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Key Findings</h4>
            <div className="space-y-3">
              {insights.keyFindings.map((finding: string, index: number) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-green-800">{finding}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {insights.recommendations && insights.recommendations.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Recommendations</h4>
            <div className="space-y-3">
              {insights.recommendations.map((recommendation: string, index: number) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                  <Target className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                  <span className="text-purple-800 dark:text-purple-200">{recommendation}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {insights.trends && insights.trends.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Trends</h4>
            <div className="space-y-3">
              {insights.trends.map((trend: string, index: number) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                  <span className="text-orange-800 dark:text-orange-200">{trend}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    );
  };

  const ReportTypeIcon = getReportTypeIcon(reportType);
  const color = getReportTypeColor(reportType);

  return (
    <div className="space-y-8">
      {/* Enhanced Report Header */}
      <div className="text-center">
        <div className={`w-16 h-16 bg-gradient-to-r from-${color}-500 to-${color}-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
          <ReportTypeIcon className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{reportName}</h1>
        {reportDescription && (
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-lg">{reportDescription}</p>
        )}
        <div className="flex items-center justify-center space-x-4 mt-4">
          <Badge variant="secondary" className="capitalize text-sm px-3 py-1">
            {reportType} Report
          </Badge>
          {reportData.summary?.dateRange && (
            <Badge variant="outline" className="text-sm px-3 py-1">
              {new Date(reportData.summary.dateRange.start).toLocaleDateString()} - {new Date(reportData.summary.dateRange.end).toLocaleDateString()}
            </Badge>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      {reportData.summary?.metrics && Object.keys(reportData.summary.metrics).length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Key Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(reportData.summary.metrics).map(([key, value]) => 
              renderMetricCard(key, value)
            )}
          </div>
        </div>
      )}

      {/* Google Ads Integration Note */}
      {reportData.googleAdsNote && (
        <div>
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Google Ads Integration</h3>
                <p className="text-blue-800 leading-relaxed">{reportData.googleAdsNote}</p>
                <div className="mt-3">
                  <Badge variant="outline" className="text-blue-700 border-blue-300">
                    Coming Soon
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Charts */}
      {reportData.charts && reportData.charts.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Analytics & Visualizations</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {reportData.charts.map((chart: any, index: number) => 
              renderChart(chart, index)
            )}
          </div>
        </div>
      )}

      {/* Tables */}
      {reportData.tables && reportData.tables.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Detailed Data</h2>
          <div className="space-y-6">
            {reportData.tables.map((table: any, index: number) => 
              renderTable(table, index)
            )}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {reportData.insights && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Insights</h2>
          {renderInsights(reportData.insights)}
        </div>
      )}

      {/* Custom Data for Custom Reports */}
      {reportData.customData && Object.keys(reportData.customData).length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Custom Widgets</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Object.entries(reportData.customData).map(([widgetId, widgetData]: [string, any]) => {
              
              // Handle "no data available" cases
              if (widgetData?.noDataAvailable || widgetData?.skipped) {
                return (
                  <Card key={widgetId} className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 capitalize">
                      {widgetId.replace(/[-_]/g, ' ')}
                    </h3>
                    <div className="text-center py-8 text-gray-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="font-medium">No Data Available</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {widgetData.message || 'Data source not connected'}
                      </p>
                    </div>
                  </Card>
                );
              }

              // Handle errors
              if (widgetData?.error) {
                return (
                  <Card key={widgetId} className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 capitalize">
                      {widgetId.replace(/[-_]/g, ' ')}
                    </h3>
                    <div className="text-center py-8 text-red-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                      <p>Error: {widgetData.error}</p>
                    </div>
                  </Card>
                );
              }

              // Handle object data with numeric values (metrics)
              if (typeof widgetData === 'object' && widgetData !== null && !Array.isArray(widgetData)) {
                const entries = Object.entries(widgetData);
                const numericEntries = entries.filter(([_, value]) => typeof value === 'number');
                
                if (numericEntries.length > 0) {
                  return (
                    <Card key={widgetId} className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 capitalize">
                        {widgetId.replace(/[-_]/g, ' ')}
                      </h3>
                      <div className="space-y-3">
                        {numericEntries.map(([key, value]: [string, any]) => (
                          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-700 capitalize">
                              {getMetricDisplayName(key)}
                            </span>
                            <span className="font-semibold text-gray-900 text-lg">
                              {formatMetricValue(value, key)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                }
              }

              // Handle primitive values
              return (
                <Card key={widgetId} className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 capitalize">
                    {widgetId.replace(/[-_]/g, ' ')}
                  </h3>
                  <div className="text-center py-8">
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {typeof widgetData === 'number' ? formatMetricValue(widgetData, widgetId) : String(widgetData)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Value</div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary/Overview */}
      {reportData.summary?.overview && (
        <div>
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                Summary
              </div>
            </h3>
            <div className="prose max-w-none">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">{reportData.summary.overview}</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
} 